import { zodResolver } from '@hookform/resolvers/zod';
import { type FormEventHandler, useId, useState } from 'react';
import { type Control, useForm, useWatch } from 'react-hook-form';

import {
  type VehicleSearchFormInput,
  type VehicleSearchFormValues,
  vehicleSearchFormSchema,
} from '@/schemas/vehicleSearch';

import { useVehicleSearchOptions, type VehicleSearchOptions } from './useVehicleSearchOptions';

type Args = {
  defaultValues: VehicleSearchFormInput;
  onSubmit: (values: VehicleSearchFormValues) => void;
  /** True where the extra filters start open, i.e. everywhere but the hero. */
  advancedOpenByDefault: boolean;
};

export type VehicleSearchFormState = {
  control: Control<VehicleSearchFormInput, unknown, VehicleSearchFormValues>;
  options: VehicleSearchOptions;
  /** The chosen make; the model field has nothing to offer without it. */
  make: string;
  /** A model from the previous make would silently survive the change. */
  clearModel: () => void;
  submit: FormEventHandler<HTMLFormElement>;
  advancedOpen: boolean;
  toggleAdvanced: () => void;
  /** Ties the "More filters" button to the panel it opens (§50). */
  advancedPanelId: string;
};

/** State and validation for the vehicle search, in one place (§16). */
export function useVehicleSearchForm({
  defaultValues,
  onSubmit,
  advancedOpenByDefault,
}: Args): VehicleSearchFormState {
  const { control, handleSubmit, setValue } = useForm<
    VehicleSearchFormInput,
    unknown,
    VehicleSearchFormValues
  >({
    resolver: zodResolver(vehicleSearchFormSchema),
    defaultValues,
  });
  const make = useWatch({ control, name: 'make' });
  const [advancedOpen, setAdvancedOpen] = useState(advancedOpenByDefault);

  return {
    control,
    options: useVehicleSearchOptions(make),
    make,
    clearModel: () => setValue('model', ''),
    submit: handleSubmit(onSubmit),
    advancedOpen,
    toggleAdvanced: () => setAdvancedOpen((open) => !open),
    advancedPanelId: useId(),
  };
}
